import React, { useEffect, useMemo, useState, useCallback } from "react";
import { AdminPanelPage } from "../AdminPanelPage";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { DashboardWithComponents, useDashboards } from "../../Contexts/DashboardsContext";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { DashboardComponentCard } from "./components/DashboardComponents/DashboardComponentCard";
import { BreadcrumbProps, Button, Icon, Tooltip, DashboardGrid, DashboardGridItem, DashboardGridLayout, useIsMobile, FilterPanel, ResizablePanel, DateDisplay } from "@aidkitorg/component-library";
import { DashboardComponentWithId } from "@aidkitorg/types/lib/admin-panel/dashboard";
import { useDashboardImageExport } from "./hooks/useDashboardImageExport";
import { useLocalizedStrings } from "../../Localization";
import { DashboardFilterProvider, useDashboardFilter } from "./contexts/DashboardFilterContext";
import { useDashboardFilterPanel } from "./hooks/useDashboardFilterPanel";
import { useSettings } from "../../Contexts/SettingsContext";
import { useFieldValueSuggestions } from '../shared/hooks/useFieldValueSuggestions';
import { hasAnyFilters } from "../shared/utils/filters";
import { FilterConfig } from "@aidkitorg/types/lib/admin-panel/filters";
import { DistroDashboard } from "../../DistroDashboard";

const DashboardPageInner = () => {
  const L = useLocalizedStrings()
  const history = useHistory()
  const location = useLocation()
  const { dashboard_id, dashboard_title } = useParams<{ dashboard_id: string, dashboard_title?: string }>()
  const { filterConfig: viewFilters, setFilterConfig: setViewFilters, setAvailableFields } = useDashboardFilter()
  const { filterPanelProps } = useDashboardFilterPanel(`dashboard-view-${dashboard_id}`)
  const { fields, getFields } = useSettings()
  const { fetchSuggestions } = useFieldValueSuggestions()

  const [dashboard, setDashboard] = useState<DashboardWithComponents>()
  const [components, setComponents] = useState<Array<DashboardComponentWithId>>([])
  const [isForceRefreshing, setIsForceRefreshing] = useState(false)

  const { imageRef, isGeneratingImage, handleDownloadImage } = useDashboardImageExport(dashboard_id)
  const { dashboardsById, fetchDashboardById, getDashboardById } = useDashboards()

  const isMobile = useIsMobile()

  const GRID_COLS = 6
  const GRID_WIDTH = 1200
  const GRID_ROW_HEIGHT = 160

  // Check if this is a survey dashboard using query parameter
  const searchParams = new URLSearchParams(location.search)
  const isSurveyDashboard = searchParams.get('type') === 'survey'

  // For survey dashboards, create a simple dashboard object with just the info we need
  const surveyDashboard = useMemo(() => {
    if (!isSurveyDashboard) return null

    // Use the encoded title from URL if available, otherwise fall back to dashboard_id
    const decodedTitle = dashboard_title ? decodeURIComponent(dashboard_title) : dashboard_id.replace(/-/g, ' ')

    return {
      id: dashboard_id,
      title: decodedTitle,
      created_by: 'system',
      created_at: Date.now(),
      updated_at: Date.now(),
      view_only: true,
      source: 'survey' as const,
      href: dashboard_id, // Use dashboard_id as the path for DistroDashboard
      components: [],
      lastUpdated: Date.now()
    }
  }, [dashboard_id, dashboard_title, isSurveyDashboard])

  const loadDashboard = useCallback(async (forceRefresh = false) => {
    // Skip loading for survey dashboards since they don't exist in the DB
    if (isSurveyDashboard) {
      return
    }
    await fetchDashboardById(dashboard_id, forceRefresh)
  }, [fetchDashboardById, dashboard_id, isSurveyDashboard])

  useAsyncEffect(async () => {
    await loadDashboard()
  }, [])

  // Load fields for filters
  useEffect(() => {
    getFields();
  }, [getFields]);

  useEffect(() => {
    if (isSurveyDashboard && surveyDashboard) {
      setDashboard(surveyDashboard)
      setComponents([])
    } else {
      const dashboard = dashboardsById[dashboard_id]
      if (dashboard) {
        setDashboard(dashboard)
        setComponents(dashboard.components)
      }
    }
  }, [getDashboardById, dashboard_id, dashboardsById, isSurveyDashboard, surveyDashboard])

  // Set available fields for filtering
  useEffect(() => {
    if (fields) {
      const fieldOptions = fields.map((field: any) => ({
        key: field.id,
        label: field.displayName || field.id,
        type: field.type || 'text'
      }))
      setAvailableFields(fieldOptions)
    }
  }, [fields, setAvailableFields])

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.dashboards,
      href: '/admin-panel/dashboards'
    },
    ...((!dashboard) ? [] : [{
      label: dashboard.title
    }])
  ]

  // Handle view filter changes
  const handleViewFilterChange = useCallback((newFilterConfig: FilterConfig) => {
    // Only process filters if they're disabled or have complete conditions
    const hasComplete = hasAnyFilters(newFilterConfig);
    const normalizedFilters = (newFilterConfig.enabled && hasComplete) ? newFilterConfig : undefined;

    // Reset page to 1 when filters change
    setViewFilters(normalizedFilters);
  }, [setViewFilters, hasAnyFilters])

  const handleForceRefresh = useCallback(async () => {
    setIsForceRefreshing(true)
    
    await Promise.all([
      // Reload dashboard data with force refresh
      loadDashboard(true),
      // Minimum 400ms delay for UX  
      new Promise(resolve => setTimeout(resolve, 400))
    ])
    
    setIsForceRefreshing(false)
  }, [loadDashboard])
  
  const pageActionButtons = [
    <Tooltip text={L.admin_panel.payments.refresh_data} asChild key="refresh">
      <Button 
        loading={isForceRefreshing}
        className="png-hide"
        size="sm"
        variant="secondary"
        onClick={handleForceRefresh}
        icon={<Icon type="solid" name="ArrowPathIcon" role="decorative" />}
      />
    </Tooltip>,
    <Tooltip text={L.admin_panel.dashboards.save_as_img} asChild key="png">
      <Button 
        loading={isGeneratingImage}
        className="png-hide"
        size="sm"
        variant="secondary"
        onClick={() => handleDownloadImage(dashboard?.title || '')}
        icon={<Icon type="solid" name="PhotoIcon" role="decorative" />}
      />
    </Tooltip>
  ]

  if (!dashboard?.view_only) {
    pageActionButtons.push(
      <Button
        key="edit"
        className="png-hide"
        onClick={() => {
          history.push(`/admin-panel/dashboards/${dashboard_id}/edit`);
        }}
      >
        {L.admin_panel.dashboards.edit_dashboard}
      </Button>
    )
  }
  
  const sizeByKind: Record<string, { w: number, h: number, minW: number, minH: number }> = {
    'Hero Number':    { w: 1, h: 1, minW: 1, minH: 1 },
    'Bar Chart':      { w: 4, h: 3, minW: 2, minH: 3 },
    'Pie Chart':      { w: 4, h: 3, minW: 2, minH: 3 },
    'Map':            { w: 5, h: 4, minW: 2, minH: 3 },
    'Summary Table':  { w: 5, h: 4, minW: 2, minH: 3 }
  };
  
  const getSizeForKind = (kind: string) => sizeByKind[kind] || { w: 2, h: 2 };
  
  const gridItems: Array<DashboardGridItem<DashboardComponentWithId>> = useMemo(() => {
    return components.map((component) => ({
      id: component.id,
      data: component,
      layout: component.style || {
        i: component.id,
        x: 0,
        y: 0,
        w: getSizeForKind(component.config.kind).w,
        h: getSizeForKind(component.config.kind).h,
        minW: getSizeForKind(component.config.kind).minW,
        minH: getSizeForKind(component.config.kind).minH,
        static: true,
      }
    }));
  }, [components]);

  const renderGridItemCallback = useCallback((item: DashboardGridItem<DashboardComponentWithId>, dimensions: { width: number; height: number }) => {
    return <DashboardComponentCard
      id={item.data.id}
      component={item.data.config}
      width={dimensions.width}
      height={dimensions.height}
      viewFilters={viewFilters}
      dashboardFilter={dashboard?.filter}
    />
  }, [viewFilters, dashboard?.filter])


  const dashboardEmptyState = (        
    <div className="flex-grow flex items-center justify-center h-64 text-gray-500">
      <div className="text-center">
        <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
        </svg>
        <div className="text-lg font-medium mb-2">{L.admin_panel.dashboards.empty_dashboard}</div>
        <div>{L.admin_panel.dashboards.empty_dashboard_msg}</div>
      </div>
    </div>
  )

  // Sort components for mobile single-column layout (left-to-right, top-to-bottom)
  const sortedComponentsForMobile = useMemo(() => {
    return [...gridItems].sort((a, b) => {
      const aY = a.layout?.y || 0;
      const bY = b.layout?.y || 0;
      const aX = a.layout?.x || 0;
      const bX = b.layout?.x || 0;
    
      if (aY !== bY) {
        return aY - bY; // Sort by row first
      }
      return aX - bX; // Then by column
    })}, [gridItems])

  const description = <div>
    {dashboard?.description}
    <div>
      {!isSurveyDashboard && dashboard?.lastUpdated && 
          <div className="font-medium text-xs text-gray-500 ">
            <span>{L.admin_panel.payments.last_updated}:&nbsp;</span>
            <DateDisplay className="text-gray-500 text-xs w-auto self-start" utcValue={dashboard?.lastUpdated} />
          </div>}
    </div>
  </div>
  
  // If this is a survey dashboard, render it using DistroDashboard approach
  if (dashboard?.source === 'survey' && dashboard.href) {
    // Use the dashboard_id directly as the path for DistroDashboard
    const dashboardPath = dashboard.href;

    return <AdminPanelPage
      ref={imageRef}
      title={dashboard?.title || ''}
      description={description}
      breadcrumbs={breadcrumbs}
      keyboardShortcuts={[
        { key: 'R', description: L.admin_panel.dashboards.refresh_dashboard, modifier: 'meta', callback: handleForceRefresh },
        { key: 'B', description: L.admin_panel.reports.toggle_sidebar, modifier: 'meta' },
        { key: 'K', description: L.admin_panel.command_palette.keyboard_shortcut, modifier: 'meta' },
        {
          displayKey: '↑', key: 'ArrowUp', description: L.admin_panel.dashboards.go_to_dashboards, modifier: 'meta', callback: () => { history.push('/admin-panel/dashboards') }
        },
      ]}
    >
      <div className="space-y-4 w-full h-full">
        <DistroDashboard hideTitle dashboard={dashboardPath} isEmbedded={true} />
      </div>
    </AdminPanelPage>
  }

  return <AdminPanelPage
    ref={imageRef}
    title={dashboard?.title || ''}
    description={description}
    pageActionButtons={pageActionButtons}
    breadcrumbs={breadcrumbs}
    keyboardShortcuts={[
      { key: 'E', description: L.admin_panel.dashboards.edit_dashboard_keyboard_shortcut, modifier: 'meta', callback: () => { history.push(`/admin-panel/dashboards/${dashboard_id}/edit`) } },
      { key: 'R', description: L.admin_panel.dashboards.refresh_dashboard, modifier: 'meta', callback: handleForceRefresh },
      { key: 'B', description: L.admin_panel.reports.toggle_sidebar, modifier: 'meta' },
      { key: 'K', description: L.admin_panel.command_palette.keyboard_shortcut, modifier: 'meta' },
      {
        displayKey: '↑', key: 'ArrowUp', description: L.admin_panel.dashboards.go_to_dashboards, modifier: 'meta', callback: () => { history.push('/admin-panel/dashboards') }
      },
    ]}
  >
    <div className="space-y-4 w-full h-full">
      <FilterPanel
        {...filterPanelProps}
        onConfirm={handleViewFilterChange}
        fetchFieldValues={fetchSuggestions}
      />
      {isMobile ? (
        <div className="flex-grow">
          {components.length === 0 ? (
            dashboardEmptyState
          ) : (
            <div className="space-y-2 p-2">
              {sortedComponentsForMobile.map((item) => (
                <div key={item.id} className="w-full">
                  <DashboardComponentCard
                    id={item.data.id}
                    component={item.data.config}
                    width={window.innerWidth - 32} // Full width minus padding
                    height={GRID_ROW_HEIGHT * (item.layout?.h || 2)} // Maintain proportional height
                    viewFilters={viewFilters}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex-grow">
          <DashboardGrid
            items={gridItems}
            renderItem={renderGridItemCallback}
            rowHeight={GRID_ROW_HEIGHT}
            isDraggable={false}
            isResizable={false}
            defaultGridCols={GRID_COLS}
            defaultGridWidth={GRID_WIDTH}
            emptyState={dashboardEmptyState}
          />
        </div>
      )}
    </div>
  </AdminPanelPage>
}

export const DashboardPage = () => {
  return (
    <DashboardFilterProvider 
      enableUrlFilters={true}
    >
      <DashboardPageInner />
    </DashboardFilterProvider>
  )
}
